import { User } from '@/api';
import useAuth from '@/contexts/AuthContext';
import useUnAuth from '@/contexts/UnAuthContext';
import { getSkipOnboardingFromLocalStorage, getTempSkipOnboarding } from '@/helpers/SkipOnboarding';
import useCart from '@/hooks/useCart';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';

/**
 * @param options.skipOnboarding Caller-declared opt-out, for a surface that
 * knows it has to keep the visitor where they are — e.g. the form builder's
 * sign-in gate, which sits mid-wizard. Passed in rather than left to the
 * storage flags below: those are a tab-wide side channel written by a mount
 * effect, so the surface rendering the form can't be sure it landed.
 */
export function useOnAccountCreated(isMobile?: boolean, options?: { skipOnboarding?: boolean }) {
    const { cart } = useCart();
    const hasCart = cart && cart.items.length > 0;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { setUser } = useAuth();
    const { authRedirect } = useUnAuth();

    return (user: User) => {
        setUser(user);

        // Any one of these means "don't send them to onboarding": the caller
        // said so, this tab's session flag, or the landing-page setting.
        const skipOnboarding = options?.skipOnboarding === true || getTempSkipOnboarding() === true || getSkipOnboardingFromLocalStorage() === true;

        // show onbarding
        if (!hasCart && !skipOnboarding && process.env.NEXT_PUBLIC_ONBOARDING && process.env.NEXT_PUBLIC_ONBOARDING === '1') {
            let returnUrl = searchParams.get('redirect');
            if (!returnUrl && !isMobile) {
                if (!pathname.startsWith('/mobile')) {
                    returnUrl = authRedirect || pathname;
                }
            }
            if (returnUrl) {
                const params = new URLSearchParams(searchParams.toString());
                params.set('returnUrl', returnUrl);
                const signupUrl = `/onboarding?${params.toString()}`;
                router.push(signupUrl);
                return;
            }
            router.push('/onboarding');
        } else if (searchParams.get('redirect')) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            router.push(searchParams.get('redirect')!);
        } else if (isMobile) {
            router.push('/');
        }
    };
}
